import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import * as mammoth from "mammoth/mammoth.browser";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import Modal from "@/components/Modal";
import ZButton from "@/components/ZButton";
import { Upload, FileSpreadsheet, Download, AlertTriangle, CheckCircle2, Trash2 } from "lucide-react";

const norm = (s) => (s || "").toString().trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const ALIASES = {
  name: ["nom", "name", "cadeau", "intitule", "libelle", "titre", "article"],
  category: ["categorie", "category", "rayon", "type"],
  price: ["prix", "price", "montant", "prix unitaire", "cout"],
  quantity: ["quantite", "qty", "qte", "quantity", "nombre", "nb"],
  description: ["description", "desc", "details", "detail"],
  image_url: ["image", "image_url", "photo", "url image", "lien image", "image url"],
  purchase_url: ["lien", "lien achat", "lien d'achat", "url", "purchase_url", "boutique", "magasin", "lien achat", "lien produit"],
};

function mapKey(header) {
  const h = norm(header);
  for (const [field, names] of Object.entries(ALIASES)) {
    if (names.includes(h)) return field;
  }
  return null;
}

function parseNum(v) {
  if (typeof v === "number") return v;
  if (!v) return 0;
  let s = v.toString().replace(/[^\d.,-]/g, "").trim();
  if (s.includes(",") && !s.includes(".")) s = s.replace(",", ".");
  else s = s.replace(/,/g, "");
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function mapRecord(raw) {
  const out = { name: "", category: "", description: "", image_url: "", purchase_url: "", price: 0, quantity: 1 };
  for (const [k, v] of Object.entries(raw)) {
    const field = mapKey(k);
    if (!field) continue;
    if (field === "price") out.price = parseNum(v);
    else if (field === "quantity") out.quantity = Math.max(1, parseInt(parseNum(v)) || 1);
    else out[field] = (v ?? "").toString().trim();
  }
  return out;
}

function rowsFromWorkbook(wb) {
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
  return json.map(mapRecord);
}

// Parse a single free-text line into a gift: "Nom - 45 000 GNF" / "Nom : 45000" / "Nom"
function parseLine(line) {
  let s = (line || "").replace(/^[\s•\-*▪◦●·]+/, "").replace(/^\d+[.)]\s*/, "").trim();
  if (!s) return null;
  const m = s.match(/^(.*?)[\s:–—\-]+([\d][\d\s.,]*)\s*(gnf|fcfa|xof|€|eur|f)?\.?$/i);
  if (m && m[1].trim().length >= 1) {
    return { name: m[1].trim(), category: "", description: "", image_url: "", purchase_url: "", price: parseNum(m[2]), quantity: 1 };
  }
  return { name: s, category: "", description: "", image_url: "", purchase_url: "", price: 0, quantity: 1 };
}

// Parse a Word (.docx) document converted to HTML: use the first table if present,
// otherwise treat each paragraph / list item as one gift line.
function rowsFromDocxHtml(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const table = doc.querySelector("table");
  if (table) {
    const trs = Array.from(table.querySelectorAll("tr"));
    if (trs.length >= 2) {
      const headers = Array.from(trs[0].querySelectorAll("th,td")).map((c) => c.textContent.trim());
      const records = trs.slice(1).map((tr) => {
        const cells = Array.from(tr.querySelectorAll("td,th"));
        const rec = {};
        headers.forEach((h, i) => { rec[h] = cells[i] ? cells[i].textContent.trim() : ""; });
        return rec;
      });
      return records.map(mapRecord);
    }
  }
  const lines = Array.from(doc.querySelectorAll("p, li"))
    .map((el) => el.textContent.trim())
    .filter(Boolean);
  return lines.map(parseLine).filter(Boolean);
}

const TEMPLATE = "Nom,Catégorie,Prix,Quantité,Description,Image,Lien d'achat\nService à thé,Maison,45000,1,Théière en porcelaine,,https://exemple.com/the\nWeek-end surprise,Voyage,150000,1,Cagnotte week-end,,";

export default function GiftImportModal({ open, onClose, listId, categories = [], onImported }) {
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [pasteText, setPasteText] = useState("");
  const [defaultCategory, setDefaultCategory] = useState(categories[0] || "Autres");
  const [importing, setImporting] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const reset = () => { setRows([]); setPasteText(""); if (fileRef.current) fileRef.current.value = ""; };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isDocx = /\.docx?$/i.test(file.name) || file.type.includes("word") || file.type.includes("officedocument.wordprocessing");
    const isPdf = /\.pdf$/i.test(file.name) || file.type === "application/pdf";
    try {
      if (isPdf) {
        setRows([]);
        setPdfLoading(true);
        const dataUrl = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(file);
        });
        const res = await api.post(`/gift-lists/${listId}/import/parse-pdf`, { pdf_base64: dataUrl });
        setRows(res.data.rows || []);
        if (!res.data.rows?.length) toast.error("Aucun cadeau détecté dans le PDF.");
        else if (res.data.method === "ai") toast.success(`PDF scanné lu par IA — ${res.data.rows.length} cadeau(x) détecté(s).`);
        return;
      }
      const buf = await file.arrayBuffer();
      let parsed;
      if (isDocx) {
        const r = await mammoth.convertToHtml({ arrayBuffer: buf });
        parsed = rowsFromDocxHtml(r.value || "");
      } else {
        const wb = XLSX.read(buf, { type: "array" });
        parsed = rowsFromWorkbook(wb);
      }
      setRows(parsed);
      if (parsed.length === 0) toast.error("Aucun cadeau détecté dans le fichier.");
    } catch (err) {
      toast.error(errMsg(err) || "Impossible de lire le fichier. Formats : CSV, Excel, Word ou PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  const handleParsePaste = () => {
    if (!pasteText.trim()) return;
    try {
      const wb = XLSX.read(pasteText, { type: "string" });
      const parsed = rowsFromWorkbook(wb);
      if (parsed.length === 0) { toast.error("Aucune ligne détectée. Ajoutez une ligne d'en-tête (Nom, Catégorie, Prix…)."); return; }
      setRows(parsed);
    } catch {
      toast.error("Format non reconnu. Collez un tableau avec en-têtes séparés par des virgules.");
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob(["\ufeff" + TEMPLATE], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "modele-liste-cadeaux.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const valid = rows.filter((r) => r.name && r.name.trim().length >= 1);
  const invalid = rows.length - valid.length;
  const hasConfidence = rows.some((r) => typeof r.confidence === "number");
  const lowConfCount = rows.filter((r) => typeof r.confidence === "number" && r.confidence < 0.6 && r.name?.trim()).length;

  const updateRow = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const confBadge = (c) => {
    const pct = Math.round(c * 100);
    const color = c >= 0.8 ? "var(--z-teal)" : c >= 0.6 ? "var(--z-orange,#DA7101)" : "var(--z-error)";
    const label = c >= 0.8 ? "Fiable" : c >= 0.6 ? "À vérifier" : "Douteux";
    return (
      <span title={`Confiance IA : ${pct}%`} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color, whiteSpace: "nowrap" }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, display: "inline-block" }} />
        {label} {pct}%
      </span>
    );
  };

  const doImport = async () => {
    if (valid.length === 0) { toast.error("Aucun cadeau valide à importer (le nom est obligatoire)."); return; }
    setImporting(true);
    try {
      const payload = {
        default_category: defaultCategory || "Autres",
        items: valid.slice(0, 500).map((r) => ({
          name: r.name.trim(),
          category: (r.category || "").trim(),
          description: (r.description || "").trim(),
          image_url: r.image_url ? r.image_url.trim() : null,
          purchase_url: r.purchase_url ? r.purchase_url.trim() : null,
          price: Number(r.price) || 0,
          quantity: parseInt(r.quantity) || 1,
          mode: "any",
        })),
      };
      const res = await api.post(`/gift-lists/${listId}/items/bulk`, payload);
      toast.success(`${res.data.imported} cadeau(x) importé(s).`);
      reset();
      onImported?.(res.data);
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Importer des cadeaux" testId="gift-import-modal">
      <div style={{ display: "grid", gap: 14 }}>
        <p style={{ fontSize: 13.5, color: "var(--z-muted)", margin: 0 }}>
          Importez votre liste depuis un fichier <strong>CSV, Excel, Word ou PDF</strong>, ou collez un tableau.
          Les PDF <strong>scannés/photographiés</strong> sont lus automatiquement par IA.
        </p>

        <button onClick={downloadTemplate} className="z-link" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 13, color: "var(--z-teal)" }} data-testid="download-template-btn">
          <Download size={15} /> Télécharger le modèle CSV
        </button>

        {/* File upload */}
        <label className="z-btn z-btn-outline" style={{ cursor: pdfLoading ? "wait" : "pointer", justifyContent: "center", opacity: pdfLoading ? 0.6 : 1 }} data-testid="gift-import-file-label">
          <Upload size={16} /> {pdfLoading ? "Lecture du PDF en cours…" : "Choisir un fichier (.csv, .xlsx, .docx, .pdf)"}
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.docx,.pdf" onChange={handleFile} disabled={pdfLoading} style={{ display: "none" }} data-testid="gift-import-file-input" />
        </label>
        {pdfLoading && (
          <div style={{ textAlign: "center", fontSize: 13, color: "var(--z-muted)" }} data-testid="pdf-loading">
            <span className="z-loader z-loader-dark" style={{ width: 18, height: 18, marginRight: 6, verticalAlign: "middle" }} />
            Analyse du PDF (extraction du texte, ou IA si scanné)…
          </div>
        )}

        {/* Paste */}
        <div>
          <label className="z-label" style={{ fontSize: 12 }}>… ou collez votre tableau (avec ligne d'en-tête)</label>
          <textarea
            className="z-input" rows={4} value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={"Nom,Catégorie,Prix,Quantité\nService à thé,Maison,45000,1"}
            data-testid="gift-import-paste"
            style={{ fontFamily: "monospace", fontSize: 12 }}
          />
          <ZButton variant="outline" size="sm" onClick={handleParsePaste} data-testid="gift-import-parse-paste">Analyser le texte collé</ZButton>
        </div>

        {/* Preview */}
        {rows.length > 0 && (
          <div data-testid="gift-import-preview">
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8, fontSize: 13, flexWrap: "wrap" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--z-teal)", fontWeight: 700 }}>
                <CheckCircle2 size={15} /> {valid.length} valide(s)
              </span>
              {invalid > 0 && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--z-error)", fontWeight: 700 }}>
                  <AlertTriangle size={15} /> {invalid} ignoré(s) (sans nom)
                </span>
              )}
              {lowConfCount > 0 && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--z-orange,#DA7101)", fontWeight: 700 }} data-testid="gift-import-lowconf-count">
                  <AlertTriangle size={15} /> {lowConfCount} à vérifier
                </span>
              )}
            </div>

            {hasConfidence && (
              <p style={{ fontSize: 12, color: "var(--z-muted)", margin: "0 0 10px", display: "flex", alignItems: "center", gap: 6 }} data-testid="gift-import-ai-hint">
                <AlertTriangle size={13} style={{ color: "var(--z-orange,#DA7101)" }} />
                Lecture par IA — vérifiez et corrigez les lignes ci-dessous avant d'importer.
              </p>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 12, color: "var(--z-muted)" }}>Catégorie par défaut :</span>
              <select className="z-input" style={{ width: "auto", padding: "4px 8px", margin: 0, fontSize: 13 }} value={defaultCategory} onChange={(e) => setDefaultCategory(e.target.value)} data-testid="gift-import-default-cat">
                {(categories.length ? categories : ["Autres"]).map((c) => <option key={c} value={c}>{c}</option>)}
                {!categories.includes("Autres") && <option value="Autres">Autres</option>}
              </select>
            </div>

            <div style={{ maxHeight: 280, overflowY: "auto", border: "1px solid var(--z-border,#eee)", borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead>
                  <tr style={{ background: "var(--z-bg-soft,#f5f5f5)", textAlign: "left", position: "sticky", top: 0 }}>
                    <th style={{ padding: "6px 8px" }}>Nom</th>
                    <th style={{ padding: "6px 8px" }}>Catégorie</th>
                    <th style={{ padding: "6px 8px", textAlign: "right" }}>Prix</th>
                    <th style={{ padding: "6px 8px", textAlign: "center" }}>Qté</th>
                    {hasConfidence && <th style={{ padding: "6px 8px" }}>Confiance</th>}
                    <th style={{ padding: "6px 8px", width: 32 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 100).map((r, i) => {
                    const low = typeof r.confidence === "number" && r.confidence < 0.6;
                    return (
                      <tr key={i} style={{ borderTop: "1px solid var(--z-border,#eee)", background: low ? "rgba(218,113,1,0.06)" : "transparent" }} data-testid={`gift-import-row-${i}`}>
                        <td style={{ padding: "3px 6px" }}>
                          <input className="z-input" value={r.name || ""} onChange={(e) => updateRow(i, "name", e.target.value)} placeholder="(nom requis)" data-testid={`gift-import-row-name-${i}`} style={{ margin: 0, padding: "4px 6px", fontSize: 12.5, width: "100%", border: r.name?.trim() ? undefined : "1px solid var(--z-error)" }} />
                        </td>
                        <td style={{ padding: "3px 6px" }}>
                          <input className="z-input" value={r.category || ""} onChange={(e) => updateRow(i, "category", e.target.value)} placeholder={defaultCategory} data-testid={`gift-import-row-cat-${i}`} style={{ margin: 0, padding: "4px 6px", fontSize: 12.5, width: "100%" }} />
                        </td>
                        <td style={{ padding: "3px 6px" }}>
                          <input className="z-input" type="number" min="0" value={r.price || 0} onChange={(e) => updateRow(i, "price", parseNum(e.target.value))} data-testid={`gift-import-row-price-${i}`} style={{ margin: 0, padding: "4px 6px", fontSize: 12.5, width: 90, textAlign: "right" }} />
                        </td>
                        <td style={{ padding: "3px 6px", textAlign: "center" }}>
                          <input className="z-input" type="number" min="1" value={r.quantity || 1} onChange={(e) => updateRow(i, "quantity", Math.max(1, parseInt(e.target.value) || 1))} data-testid={`gift-import-row-qty-${i}`} style={{ margin: 0, padding: "4px 6px", fontSize: 12.5, width: 56, textAlign: "center" }} />
                        </td>
                        {hasConfidence && (
                          <td style={{ padding: "3px 6px" }}>{typeof r.confidence === "number" ? confBadge(r.confidence) : null}</td>
                        )}
                        <td style={{ padding: "3px 6px", textAlign: "center" }}>
                          <button onClick={() => removeRow(i)} title="Supprimer cette ligne" data-testid={`gift-import-row-del-${i}`} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--z-error)", padding: 4, display: "inline-flex" }}>
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <ZButton block loading={importing} disabled={valid.length === 0} onClick={doImport} data-testid="gift-import-confirm-btn">
          {valid.length > 0 ? `Importer ${valid.length} cadeau(x)` : "Importer"}
        </ZButton>
      </div>
    </Modal>
  );
}
